import {Block, BlockBase, ContentData, MediaElement} from "./interfaces.ts";

export const isBlockTypeCorrect = (block: BlockBase<unknown, unknown>, type: string): boolean => {
    const isTypeCorrect: boolean = type === block.type;

    if (!isTypeCorrect) {
        console.error(`Expected a text block, but got ${block.type}`);
        return false;
    }
    return true
}

export const getContentValue = <BlockProperties, ContentDataType>(locale: string, fallbackLocale: string, block: BlockBase<BlockProperties, ContentDataType>, contentData?: ContentData<ContentDataType> | null): ContentDataType => {
    if (!block.defaultContent) {
        throw new Error("No default content");
    }

    let data: ContentDataType = { ...(block.defaultContent[fallbackLocale] as object) } as ContentDataType;

    if (locale !== fallbackLocale && block.defaultContent[locale]) {
        data = { ...data, ...(block.defaultContent[locale] as object) };
    }

    if (!contentData || (!contentData[fallbackLocale] && !contentData[locale])) {
        return data;
    }

    if (contentData[fallbackLocale] && contentData[fallbackLocale][block.id]) {
        // @ts-expect-error handle replacements
        for (const [propertyKey, propertyValue] of Object.entries(contentData[fallbackLocale][block.id])) {
            if (propertyValue !== null && propertyValue !== undefined) {
                data = {...data, [propertyKey]: propertyValue}
            }
        }
    }


    if (contentData[locale] && contentData[locale][block.id]) {
        // @ts-expect-error handle replacements
        for (const [propertyKey, propertyValue] of Object.entries(contentData[locale][block.id])) {
            data = {...data, [propertyKey]: propertyValue}
        }
    }

    return data;
}

export const deleteBlock = (blocks: Block[], blockId: string): Block[] => {
  return blocks.reduce((acc, block) => {
    if (block.id === blockId) {
      return acc;
    }

    if (block.type === 'container' && block.properties.children) {
      const newChildren = block.properties.children.map((childBlocks: Block[]) =>
        deleteBlock(childBlocks, blockId),
      );
      acc.push({
        ...block,
        properties: {
          ...block.properties,
          children: newChildren,
        },
      });
    } else {
      acc.push(block);
    }

    return acc;
  }, [] as Block[]);
};

export const updateBlock = (blocks: Block[], updatedBlock: Block): Block[] => {
  return blocks.map(block => {
    if (block.id === updatedBlock.id) {
      return updatedBlock;
    }

    if (block.type === 'container' && block.properties.children) {
      const newChildren = block.properties.children.map((childBlocks: Block[]) =>
        updateBlock(childBlocks, updatedBlock)
      );
      return {
        ...block,
        properties: {
          ...block.properties,
          children: newChildren,
        },
      };
    }

    return block;
  });
};

export const getUsedContentTypes = (blocks: Block[]): string[] => {
  return blocks.reduce((acc, block) => {
    if (block.properties?.contentType) {
      acc.push(block.properties.contentType);
    }

    if (block.type === 'container' && block.properties?.children) {
      const nestedContentTypes = block.properties.children.flatMap((childBlocks: Block[]) =>
        getUsedContentTypes(childBlocks)
      );
      acc.push(...nestedContentTypes);
    }

    return acc;
  }, [] as string[]);
};

/**
 * Resolves the actual src URL from a given src string.
 * If src is absolute (http/https), returns it directly.
 * If src is relative (e.g., /company_layout_media_elements/406080#default),
 * looks up the file in the files array and returns the appropriate format src.
 * 
 * @param src The source URL to resolve
 * @param files Optional array of MediaElement files
 * @returns The resolved URL or the original src if not found
 */
export const resolveSrc = (src: string | undefined, files?: MediaElement[]): string | undefined => {
  if (!src) return src;

  // Absolute URLs are returned unchanged
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  // New format: starts with '@' followed by an ID and optional #format
  if (src.startsWith('@')) {
    const idPart = src.slice(1);
    const [idStr, formatName = 'default'] = idPart.split('#');
    if (!files) {
      return src;
    }
    const file = files.find(f => f['@id'] === idStr);
    if (!file) {
      return src;
    }
    const format = file.formats.find(f => f.format === formatName);
    return format ? format.src : src;
  }

  // Fallback: return original src
  return src;
}

/**
* @param url The YouTube URL to convert.
* @param muted Whether the embedded video should be muted. Defaults to false.
* @returns The embeddable YouTube URL string, or null if no valid ID is found.
*/
export const getYouTubeEmbedUrl = (url: string, muted: boolean = false): string | null => {
  let videoId: string | null = null;

  // This comprehensive regex attempts to capture the 11-character video ID
  // from various YouTube URL formats.
  const regex = /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);

  if (match && match[1]) {
    videoId = match[1];
  }

  if (!videoId) {
    return null;
  }

  // Construct the final URL using a template literal
  const baseEmbedUrl = `https://www.youtube.com/embed/${videoId}`;
  const params = `?autoplay=1&loop=1&controls=0&showinfo=0&playlist=${videoId}`;
  const mutedParam = muted ? '&mute=1' : '';

  return `${baseEmbedUrl}${params}${mutedParam}`;
}

/**
 * Extracts all media file IDs referenced in a src string.
 * Supports both /company_layout_media_elements/{id} and /job_offer_media_elements/{id} formats.
 * 
 * @param src The source URL to extract file ID from
 * @returns The file ID if found, null otherwise
 */
export const extractFileIdFromSrc = (src: string | undefined): string | null => {
  if (!src) return null;

  // Absolute URLs have no file ID
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return null;
  }

  // New format: starts with '@' followed by an ID (string) and optional #format
  if (src.startsWith('@')) {
    const idPart = src.slice(1);
    const [idStr] = idPart.split('#');
    return idStr || null;
  }

  // Fallback: no ID
  return null;
};

/**
 * Recursively extracts all media file IDs from blocks.
 * 
 * @param blocks Array of blocks to search
 * @returns Set of unique file IDs
 */
const extractFileIdsFromBlocks = (blocks: Block[]): Set<number|string> => {
  const fileIds = new Set<string|number>();
  
  for (const block of blocks) {
    // Check defaultContent for src fields
    if (block.defaultContent) {
      for (const localeContent of Object.values(block.defaultContent)) {
        if (localeContent && typeof localeContent === 'object') {
          // Check for src field
          if ('src' in localeContent && typeof localeContent.src === 'string') {
            const fileId = extractFileIdFromSrc(localeContent.src);
            if (fileId !== null) {
              fileIds.add(fileId);
            }
          }
          
          // Check for gallerySrc field (array of src)
          if ('gallerySrc' in localeContent && Array.isArray(localeContent.gallerySrc)) {
            for (const src of localeContent.gallerySrc) {
              if (typeof src === 'string') {
                const fileId = extractFileIdFromSrc(src);
                if (fileId !== null) {
                  fileIds.add(fileId);
                }
              }
            }
          }
        }
      }
    }
    
    // Recursively check container children
    if (block.type === 'container' && block.properties?.children) {
      for (const childBlocks of block.properties.children) {
        if (Array.isArray(childBlocks)) {
          const childFileIds = extractFileIdsFromBlocks(childBlocks);
          childFileIds.forEach(id => fileIds.add(id));
        }
      }
    }
  }
  
  return fileIds;
};

/**
 * Extracts all media file IDs from content data.
 * 
 * @param contentData Content data to search
 * @returns Set of unique file IDs
 */
const extractFileIdsFromContent = (contentData: ContentData<unknown>): Set<string> => {
  const fileIds = new Set<string>();
  
  for (const localeData of Object.values(contentData)) {
    if (localeData && typeof localeData === 'object') {
      for (const blockContent of Object.values(localeData)) {
        if (blockContent && typeof blockContent === 'object') {
          // Check for src field
          if ('src' in blockContent && typeof blockContent.src === 'string') {
            const fileId = extractFileIdFromSrc(blockContent.src);
            if (fileId !== null) {
              fileIds.add(fileId);
            }
          }
          
          // Check for gallerySrc field (array of src)
          if ('gallerySrc' in blockContent && Array.isArray(blockContent.gallerySrc)) {
            for (const src of blockContent.gallerySrc) {
              if (typeof src === 'string') {
                const fileId = extractFileIdFromSrc(src);
                if (fileId !== null) {
                  fileIds.add(fileId);
                }
              }
            }
          }
        }
      }
    }
  }
  
  return fileIds;
};

/**
 * Extracts all referenced media file IDs from template blocks and content data.
 * Returns only the IDs that are currently in use.
 * 
 * @param blocks Template blocks
 * @param contentData Optional content data
 * @returns Array of unique file IDs that are referenced
 */
export const extractReferencedFileIds = (blocks: Block[], contentData?: ContentData<unknown>): (string | number)[] => {
  const fileIds = new Set<string | number>();
  
  // Extract from blocks
  const blockFileIds = extractFileIdsFromBlocks(blocks);
  blockFileIds.forEach(id => fileIds.add(id));
  
  // Extract from content data if provided
  if (contentData) {
    const contentFileIds = extractFileIdsFromContent(contentData);
    contentFileIds.forEach(id => fileIds.add(id));
  }
  
  // Convert to array; sorting numeric IDs ascending while keeping strings unchanged
  const arr = Array.from(fileIds);
  return arr.sort((a, b) => {
    const aNum = typeof a === 'number' ? a : NaN;
    const bNum = typeof b === 'number' ? b : NaN;
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    return 0;
  });
};

/**
 * Filters the files array to only include MediaElements that are referenced in the template blocks.
 * 
 * @param blocks Template blocks
 * @param files Array of available MediaElements
 * @returns Filtered array of MediaElements that are referenced in the blocks
 */
export const filterReferencedFiles = (blocks: Block[], files?: MediaElement[]): MediaElement[] => {
  if (!files || files.length === 0) {
    return [];
  }
  
  const referencedIds = extractFileIdsFromBlocks(blocks);
  return files.filter(file => referencedIds.has(file.id));
};

/**
 * Filters the files array to only include MediaElements that are referenced in both template blocks and content data.
 * 
 * @param blocks Template blocks
 * @param contentData Content data
 * @param files Array of available MediaElements
 * @returns Filtered array of MediaElements that are referenced in the blocks and content
 */
export const filterReferencedFilesWithContent = (
  blocks: Block[], 
  contentData: ContentData<unknown>, 
  files?: MediaElement[]
): MediaElement[] => {
  if (!files || files.length === 0) {
    return [];
  }
  
  const referencedIds = new Set<string|number>();
  
  // Extract from blocks
  const blockFileIds = extractFileIdsFromBlocks(blocks);
  blockFileIds.forEach(id => referencedIds.add(id));

  // Extract from content data
  const contentFileIds = extractFileIdsFromContent(contentData);
  contentFileIds.forEach(id => referencedIds.add(id));

  return files.filter(file => referencedIds.has(file['@id']));
};
